import { useMemo, useState } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { useAuth } from "@/contexts/AuthContext";
import { useDeleteDocument } from "@/hooks/useDeleteDocument";
import { useUpdateDocument } from "@/hooks/useUpdateDocument";
import { ConfirmDialog } from "@/components/shared";
import {
  DocumentTypeEnum,
  parseDocumentType,
  DOCUMENT_TYPE_ENUM_LABELS,
} from "../utils/documentTypeFromExtension";
import DocumentCard from "./DocumentCard";
import DocumentListSkeleton from "./DocumentListSkeleton";
import type { DocumentRecord } from "./EditDocumentModal";

const CATEGORY_ORDER: DocumentTypeEnum[] = [
  DocumentTypeEnum.Image,
  DocumentTypeEnum.Document,
  DocumentTypeEnum.Project,
];

function groupByCategory(
  documents: DocumentRecord[]
): Map<DocumentTypeEnum, DocumentRecord[]> {
  const map = new Map<DocumentTypeEnum, DocumentRecord[]>();
  for (const doc of documents) {
    const type = parseDocumentType(doc.documentType);
    const list = map.get(type) ?? [];
    list.push(doc);
    map.set(type, list);
  }
  return map;
}

interface DocumentListProps {
  documents: DocumentRecord[];
  projectId: string;
  onEdit: (doc: DocumentRecord) => void;
  onDelete: () => void;
  loading?: boolean;
}

export default function DocumentList({
  documents,
  projectId,
  onEdit,
  onDelete,
  loading,
}: DocumentListProps) {
  const { currentUser } = useAuth();
  const canManage =
    currentUser?.role === "ADMIN" || currentUser?.role === "COLLABORATOR";
  const [deleteId, setDeleteId] = useState<string | null>(null);
  const [confirmSharedDoc, setConfirmSharedDoc] = useState<DocumentRecord | null>(null);
  const [visibilityUpdatingId, setVisibilityUpdatingId] = useState<string | null>(null);

  const { deleteDocument, loading: deleteLoading } =
    useDeleteDocument(projectId);
  const { updateDocument, loading: updateLoading } = useUpdateDocument(projectId);

  const groups = useMemo(() => groupByCategory(documents), [documents]);

  const handleConfirmDelete = async () => {
    if (!deleteId) return;
    try {
      await deleteDocument(deleteId);
      setDeleteId(null);
      onDelete();
    } catch (err) {
      console.error(err);
    }
  };

  const handleVisibilityToggle = (doc: DocumentRecord) => {
    const isShared = doc.visibility?.toLowerCase() === "shared";
    if (isShared) {
      setVisibilityUpdatingId(doc.id);
      updateDocument({
        id: doc.id,
        visibility: "internal",
      })
        .then(() => {
          onDelete();
        })
        .catch((err) => console.error(err))
        .finally(() => setVisibilityUpdatingId(null));
    } else {
      setConfirmSharedDoc(doc);
    }
  };

  const handleConfirmShared = async () => {
    if (!confirmSharedDoc) return;
    try {
      setVisibilityUpdatingId(confirmSharedDoc.id);
      await updateDocument({
        id: confirmSharedDoc.id,
        visibility: "shared",
      });
      setConfirmSharedDoc(null);
      onDelete();
    } catch (err) {
      console.error(err);
    } finally {
      setVisibilityUpdatingId(null);
    }
  };

  if (loading) {
    return <DocumentListSkeleton />;
  }

  if (documents.length === 0) {
    return (
      <Card>
        <CardContent className="text-center py-24">
          <p className="text-primary-600 dark:text-primary-400">
            Nenhum documento ainda.
          </p>
        </CardContent>
      </Card>
    );
  }

  return (
    <>
      <div className="space-y-10">
        {CATEGORY_ORDER.map((category) => {
          const items = groups.get(category);
          if (!items?.length) return null;
          const label = DOCUMENT_TYPE_ENUM_LABELS[category];
          return (
            <section key={category} aria-labelledby={`doc-group-${category}`}>
              <h2
                id={`doc-group-${category}`}
                className="text-xs uppercase tracking-widest text-primary-600 dark:text-primary-400 mb-4"
              >
                {label}
              </h2>
              <div className="flex flex-col gap-3">
                {items.map((doc) => (
                  <DocumentCard
                    key={doc.id}
                    document={doc}
                    canManage={canManage}
                    onEdit={() => onEdit(doc)}
                    onDeleteClick={() => setDeleteId(doc.id)}
                    onVisibilityToggle={handleVisibilityToggle}
                    visibilityLoading={visibilityUpdatingId === doc.id && updateLoading}
                    deleteLoading={deleteLoading}
                  />
                ))}
              </div>
            </section>
          );
        })}
      </div>

      <ConfirmDialog
        isOpen={!!deleteId}
        onClose={() => setDeleteId(null)}
        title="Excluir documento"
        message="Tem certeza de que deseja excluir este documento?"
        confirmLabel="EXCLUIR"
        confirmLoadingLabel="EXCLUINDO..."
        onConfirm={handleConfirmDelete}
        loading={deleteLoading}
        destructive
      />

      <ConfirmDialog
        isOpen={!!confirmSharedDoc}
        onClose={() => setConfirmSharedDoc(null)}
        title="Tornar documento público?"
        message="Este documento ficará visível para todos os membros do projeto. Continuar?"
        confirmLabel="TORNAR PÚBLICO"
        confirmLoadingLabel="SALVANDO..."
        onConfirm={handleConfirmShared}
        loading={updateLoading}
        destructive={false}
      />
    </>
  );
}
